import { apiGet, apiSend } from "./client";
import type {
  EncounterSummary,
  GuildDetailResponse,
  GuildSummary,
  GuildUploadCodeSummary,
  GuildMemberSummary,
  GuildMembershipSummary,
  GuildInviteCodeSummary,
  GuildRosterResponse,
  GuildClassModerationResponse,
} from "./types";

function comparableSlug(value: string | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9`'._-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function guildMatchesSlug(guild: GuildSummary, slug: string) {
  const wanted = comparableSlug(decodeURIComponent(slug));
  return (
    comparableSlug(guild.slug) === wanted ||
    comparableSlug(guild.name) === wanted
  );
}

export async function fetchGuilds(params?: {
  serverId?: number;
  serverSlug?: string;
}) {
  const search = new URLSearchParams();
  if (params?.serverId) search.set("serverId", String(params.serverId));
  if (params?.serverSlug) search.set("serverSlug", params.serverSlug);
  const suffix = search.toString() ? `?${search.toString()}` : "";
  const data = await apiGet<{ guilds: GuildSummary[] }>(`/guilds${suffix}`);
  return data.guilds;
}

export async function fetchGuild(id: string) {
  return apiGet<GuildDetailResponse>(`/guilds/${encodeURIComponent(id)}`);
}

export async function resolveGuildByServerSlug(
  serverSlug: string,
  guildSlug: string,
) {
  const guilds = await fetchGuilds({ serverSlug });
  const guild = guilds.find((candidate) =>
    guildMatchesSlug(candidate, guildSlug),
  );

  if (!guild) {
    throw new Error(
      `Could not find guild ${decodeURIComponent(guildSlug)} on ${serverSlug}.`,
    );
  }

  return guild;
}

export async function fetchGuildByServerSlug(
  serverSlug: string,
  guildSlug: string,
) {
  const guild = await resolveGuildByServerSlug(serverSlug, guildSlug);
  return fetchGuild(guild.id);
}



export async function fetchGuildRoster(guildId: string, params?: { days?: number; limit?: number; includePets?: boolean }) {
  const search = new URLSearchParams();
  if (params?.days) search.set("days", String(params.days));
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.includePets !== undefined) search.set("includePets", String(params.includePets));
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return apiGet<GuildRosterResponse>(`/guilds/${encodeURIComponent(guildId)}/roster${suffix}`);
}

export async function fetchGuildClassModeration(guildId: string) {
  return apiGet<GuildClassModerationResponse>(`/guilds/${encodeURIComponent(guildId)}/class-moderation`);
}

export async function fetchGuildRecentEncounters(id: string, limit = 25) {
  const data = await apiGet<{ encounters: EncounterSummary[] }>(
    `/guilds/${encodeURIComponent(id)}/recent-encounters?limit=${limit}`,
  );
  return data.encounters;
}

export async function fetchAdminGuilds() {
  const data = await apiGet<{ guilds: GuildSummary[] }>("/admin/guilds");
  return data.guilds;
}

export async function createAdminGuild(input: {
  serverId: number;
  name: string;
  slug?: string;
  description?: string;
}) {
  const data = await apiSend<{ guild: GuildSummary }>(
    "/admin/guilds",
    "POST",
    input,
  );
  return data.guild;
}

export async function updateAdminGuild(
  id: string,
  input: { name?: string; slug?: string; description?: string },
) {
  const data = await apiSend<{ guild: GuildSummary }>(
    `/admin/guilds/${encodeURIComponent(id)}`,
    "PUT",
    input,
  );
  return data.guild;
}

export async function deleteAdminGuild(id: string) {
  return apiSend<void>(`/admin/guilds/${encodeURIComponent(id)}`, "DELETE");
}

export async function createGuildUploadCode(
  guildId: string,
  input: { label?: string; code?: string },
) {
  const data = await apiSend<{ uploadCode: GuildUploadCodeSummary }>(
    `/admin/guilds/${encodeURIComponent(guildId)}/upload-codes`,
    "POST",
    input,
  );
  return data.uploadCode;
}

export async function revokeGuildUploadCode(guildId: string, codeId: string) {
  const data = await apiSend<{ uploadCode: GuildUploadCodeSummary }>(
    `/admin/guilds/${encodeURIComponent(guildId)}/upload-codes/${encodeURIComponent(codeId)}`,
    "DELETE",
  );
  return data.uploadCode;
}

export async function upsertGuildMember(
  guildId: string,
  input: { userId: string; role: string },
) {
  const data = await apiSend<{ member: GuildMemberSummary }>(
    `/admin/guilds/${encodeURIComponent(guildId)}/members`,
    "PUT",
    input,
  );
  return data.member;
}

export async function removeGuildMember(guildId: string, memberId: string) {
  return apiSend<void>(
    `/admin/guilds/${encodeURIComponent(guildId)}/members/${encodeURIComponent(memberId)}`,
    "DELETE",
  );
}

export async function fetchMyGuilds() {
  const data = await apiGet<{ memberships: GuildMembershipSummary[] }>(
    "/me/guilds",
  );
  return data.memberships;
}

export async function createGuild(input: {
  serverId: number;
  name: string;
  slug?: string;
  description?: string;
}) {
  const data = await apiSend<{ guild: GuildSummary }>("/guilds", "POST", input);
  return data.guild;
}

export async function joinGuild(input: { code: string }) {
  return apiSend<{ guild: GuildSummary; membership: GuildMemberSummary }>(
    "/guilds/join",
    "POST",
    input,
  );
}

export async function leaveGuild(id: string) {
  return apiSend<void>(`/guilds/${encodeURIComponent(id)}/leave`, "POST");
}

export async function updateManagedGuild(
  guildId: string,
  input: { name?: string; slug?: string; description?: string },
) {
  const data = await apiSend<{ guild: GuildSummary }>(
    `/guilds/${encodeURIComponent(guildId)}`,
    "PUT",
    input,
  );
  return data.guild;
}

export async function upsertManagedGuildMember(
  guildId: string,
  input: { userId: string; role: string },
) {
  const data = await apiSend<{ member: GuildMemberSummary }>(
    `/guilds/${encodeURIComponent(guildId)}/members`,
    "PUT",
    input,
  );
  return data.member;
}

export async function removeManagedGuildMember(guildId: string, memberId: string) {
  return apiSend<void>(
    `/guilds/${encodeURIComponent(guildId)}/members/${encodeURIComponent(memberId)}`,
    "DELETE",
  );
}

export async function fetchGuildUploadCodes(guildId: string) {
  const data = await apiGet<{ uploadCodes: GuildUploadCodeSummary[] }>(
    `/guilds/${encodeURIComponent(guildId)}/upload-codes`,
  );
  return data.uploadCodes;
}

export async function createGuildManagedUploadCode(
  guildId: string,
  input: { label?: string; code?: string },
) {
  const data = await apiSend<{ uploadCode: GuildUploadCodeSummary }>(
    `/guilds/${encodeURIComponent(guildId)}/upload-codes`,
    "POST",
    input,
  );
  return data.uploadCode;
}

export async function revokeGuildManagedUploadCode(
  guildId: string,
  codeId: string,
) {
  const data = await apiSend<{ uploadCode: GuildUploadCodeSummary }>(
    `/guilds/${encodeURIComponent(guildId)}/upload-codes/${encodeURIComponent(codeId)}`,
    "DELETE",
  );
  return data.uploadCode;
}

export async function fetchGuildInviteCodes(guildId: string) {
  const data = await apiGet<{ inviteCodes: GuildInviteCodeSummary[] }>(
    `/guilds/${encodeURIComponent(guildId)}/invite-codes`,
  );
  return data.inviteCodes;
}

export async function createGuildInviteCode(
  guildId: string,
  input: { label?: string; code?: string },
) {
  const data = await apiSend<{ inviteCode: GuildInviteCodeSummary }>(
    `/guilds/${encodeURIComponent(guildId)}/invite-codes`,
    "POST",
    input,
  );
  return data.inviteCode;
}

export async function revokeGuildInviteCode(guildId: string, codeId: string) {
  const data = await apiSend<{ inviteCode: GuildInviteCodeSummary }>(
    `/guilds/${encodeURIComponent(guildId)}/invite-codes/${encodeURIComponent(codeId)}`,
    "DELETE",
  );
  return data.inviteCode;
}
